import { useCallback, useMemo, useState } from 'react';
import { Colors } from '../../../themes/colors';
import { getTimeOptions, getTimeValue } from './Utils';

const TIME_OPTIONS = getTimeOptions();

const DEFAULT_TIME_VALUE = getTimeValue('08:00am');

/**
 * TimePicker component for selecting time in 15-minute intervals
 * Supports both controlled and uncontrolled modes
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isError=false] - Whether to display error styling
 * @param {boolean} [props.isReadonly=false] - Whether the component is read-only (displays as text input)
 * @param {string} [props.defaultValue] - Initial time value for uncontrolled mode (e.g., "9:30am" or "09:30")
 *                                        Only used on initial render, does not respond to changes
 * @param {string} [props.value] - Time value for controlled mode (e.g., "9:30am" or "09:30")
 *                                 When provided, component enters controlled mode and responds to value changes
 * @param {Function} [props.onChange] - Callback fired when time selection changes
 *                                      Receives time object: {label: string, value: {format_12hr: string, format_24hr: string}}
 * @param {Object} [props.style] - Custom styles to apply to the input/select element (overrides default styles)
 *
 * @example
 * // Uncontrolled mode with default value
 * <TimePicker defaultValue="9:30am" onChange={(timeObj) => console.log(timeObj)} />
 *
 * @example
 * // Controlled mode
 * const [time, setTime] = useState("2:00pm");
 * <TimePicker value={time} onChange={(timeObj) => setTime(timeObj.value.format_12hr)} />
 *
 * @example
 * // Read-only mode
 * <TimePicker value="3:45pm" isReadonly />
 *
 * @example
 * // With custom styling
 * <TimePicker value="9:00am" style={{ width: '200px', fontSize: '14px' }} />
 */
export const TimePicker = ({
  isError = false,
  isReadonly = false,
  defaultValue,
  value,
  onChange,
  style,
}) => {
  const [internalValue, setInternalValue] = useState(() => {
    if (defaultValue) {
      return getTimeValue(defaultValue) || DEFAULT_TIME_VALUE;
    }
    return DEFAULT_TIME_VALUE;
  });

  // Determine if controlled mode (value prop is provided)
  const isControlled = value !== undefined;

  // Get the current value (controlled or uncontrolled)
  const currentValue = useMemo(() => {
    if (isControlled) {
      return getTimeValue(value) || DEFAULT_TIME_VALUE;
    }
    return internalValue;
  }, [isControlled, value, internalValue]);

  const getErrorStyles = useCallback(() => {
    if (!isError) return {};

    return {
      border: `1px solid ${Colors.error_400}`,
      backgroundColor: '#FEF9F9',
    };
  }, [isError]);

  const handleValueChange = useCallback(
    (e) => {
      const { selectedIndex } = e.target;

      const newValue = TIME_OPTIONS[selectedIndex];

      // Update internal value only if uncontrolled
      if (!isControlled) {
        setInternalValue(newValue);
      }

      // Call onChange callback with the full value object
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  if (isReadonly) {
    return (
      <input
        type="text"
        className="form-control"
        style={{
          backgroundColor: 'inherit!important',
          fontSize: '1.25rem',
          height: 32,
          ...style,
          ...getErrorStyles(),
        }}
        value={currentValue.label}
        readOnly
      />
    );
  }

  return (
    <select
      className="custom-select custom-select-lg"
      style={{
        height: 32,
        ...style,
        ...getErrorStyles(),
      }}
      value={currentValue.label}
      onChange={handleValueChange}
    >
      {TIME_OPTIONS.map((option) => (
        <option key={option.value.format_12hr} value={option.value.format_12hr}>
          {option.label}
        </option>
      ))}
    </select>
  );
};
